import { JSX } from 'react'

import {
  ArrowUpRight,
  ArrowLeftRight,
  Cog,
  ArrowUpFromLine,
  Layers,
  Zap,
  Plug,
} from 'lucide-react'

import { AnimatedBorder } from '~/components/AnimatedBorder'
import { ImageWithFallback } from '~/components/ImageWithFallback'
import { NetworkLogo } from '~/components/component-library/NetworkLogo'
import { VestingClaimIcon } from '~/components/smart-accounts/vesting/VestingClaimIcon'
import {
  BackendTransaction,
  TransactionStatus,
  BasicTransactionType,
} from '~/types'
import { parseTransactionMetadata } from '~/utils/parseTransactionMetadata'
import { isTransactionOrg } from '~/utils/smartAccounts'
import { cn } from '~/utils/tailwind-utils'

export const TransactionIcon = ({
  transaction,
  size = 'sm',
  networkLogo,
}: {
  transaction: BackendTransaction
  size?: 'sm' | 'lg'
  networkLogo?: boolean
}) => {
  const metadata = !transaction.isMerkleTransaction
    ? parseTransactionMetadata(transaction.transactionMetadata)
    : undefined

  const bridgeMetadata = metadata?.bridgeData

  const isBridgeFailed = bridgeMetadata && bridgeMetadata?.status === 'FAILURE'
  const isBridgePending =
    transaction.status === TransactionStatus.Succeeded &&
    bridgeMetadata &&
    bridgeMetadata?.status === 'PENDING'

  const isFailed =
    transaction.status === TransactionStatus.Failed || isBridgeFailed

  const isPending =
    transaction.status === TransactionStatus.Submitted || isBridgePending

  const getIcon = (types?: BasicTransactionType[]): JSX.Element => {
    const className = cn('rounded-lg p-2', {
      'w-9 h-9': size === 'sm',
      'w-14 h-14': size === 'lg',
      'ring-1 ring-red-600': isFailed,
    })
    const walletConnectSize = size === 'sm' ? 24 : 56
    let icon = null

    if (isTransactionOrg(transaction) && transaction.isOfframp) {
      icon = <ArrowUpFromLine className={className} />
    } else {
      const isMerkleTransaction =
        !!types?.includes(BasicTransactionType.UpdateSigners) ||
        !!types?.includes(BasicTransactionType.RecoverAccount) ||
        !!types?.includes(BasicTransactionType.AddNetworks)
      if (isMerkleTransaction) {
        icon = <Cog className={className} />
      } else if (types?.includes(BasicTransactionType.Batch)) {
        icon = <Layers className={className} />
      } else if (types?.includes(BasicTransactionType.Bridge)) {
        icon = <ArrowUpRight className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.Transfer)) {
        icon = <ArrowUpRight className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.Swap)) {
        icon = <ArrowLeftRight className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.Earn)) {
        icon = <Zap className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.Claim)) {
        icon = <VestingClaimIcon className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.SplitsConnect)) {
        icon = <Plug className={cn(className)} />
      } else if (types?.includes(BasicTransactionType.WalletConnect)) {
        icon = (
          <div className={cn(className, 'flex items-center justify-center')}>
            <ImageWithFallback
              fallbackSrc={'/wallets/walletconnect-logo.svg'}
              src={
                transaction.walletConnectAppIcon ??
                '/wallets/walletconnect-logo.svg'
              }
              alt={transaction.walletConnectAppName ?? 'wallet connect app'}
              width={walletConnectSize}
              height={walletConnectSize}
            />
          </div>
        )
      } else {
        icon = <ArrowUpRight className={className} />
      }
    }

    if (isPending) {
      return <AnimatedBorder size={size}>{icon}</AnimatedBorder>
    }

    return icon
  }

  return (
    <div className="relative rounded-lg bg-gray-100 dark:bg-gray-700">
      {getIcon(transaction.types)}
      {networkLogo && (
        <div className="absolute -bottom-0.5 -right-1">
          <NetworkLogo
            size={'sm'}
            chainIds={
              transaction.isMerkleTransaction
                ? transaction.chainIds
                : [transaction.chainId]
            }
          />
        </div>
      )}
    </div>
  )
}
